import { useState, useMemo, useCallback } from "react";

import { Button, Card, Heading } from "@aidron/aidron-ds";

import { SaleIcon } from "../../../components/SharedComponents/ListItem/ListItem.style";

import { i18n } from "../../../translate/i18n";

import { Default } from "../../../components/Default/Default";

import { CustomText, CustomHeading, PageHeading } from "../../../components/StyledComponents/styleComponents.index";

import { sumColumn } from "../../../utils/utils";
import { 
  currencyFormatter, 
  createDecimalFormatter, 
  createPercentFormatter, 
  textFormatter 
} from "../../../utils/formatter/formatter";

import { 
  ListItem,
  LoadingContent,
  ModalTemplate,
  ErrorModal,
  PredictionChart,
  LabelAndNumberView,
  ProductHierarchyHeading,
  TextAndLabelView,
  OpportunityOptions
} from "../../../components/SharedComponents/sharedComponents.index";
import { LOADING_CONTENT_TYPE } from "../../../components/SharedComponents/LoadingContent/loading.content.constants";
import { OPPORTUNITY_STATUS } from "../../../components/components.constants";

import { RemovedOpportunitiesModal } from "../Opportunities/components/RemovedOppModal";

import { buildModalConfig, renderModal } from "../../../viewModel/builder";

import { useOtbViewModel } from "../../../viewModel/useOtbVewModel";

import styled from "styled-components";
import PropTypes from 'prop-types';
import { useAnalysisViewModel } from "../../../viewModel/useAnalysysViewModel";

const CustomHeadingCheckout = styled(Heading)`
  color: ${({ color }) => {
    if (color === "grey") return "#585656";
    if (color === "black") return "#000000";
    return "#f26f21";
  }};
  padding-right: 10px;
`;

const { t } = i18n;

const CheckoutSummary = ({ ctx }) => {
  const { t } = i18n;
  const { opportunities = [] } = ctx;
  const sum = sumColumn(opportunities, "value");

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '40px', padding: '20px 10px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', borderBottom: 'solid 1px #919191', paddingBlockEnd: '40px' }}>
          <CustomHeadingCheckout size="md" color="grey">
            {textFormatter(t("checkoutSummary"), { textTransform: 'uppercase'})}
          </CustomHeadingCheckout>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', borderBottom: 'none', paddingBlockEnd: '40px' }}>
          <div className="d-flex align-items-center justify-content-center">
            <CustomHeadingCheckout size="sm" color="grey">
              {textFormatter(t("totalOpportunities"), { textTransform: 'uppercase' })}
            </CustomHeadingCheckout>

            <CustomHeadingCheckout size="lg" color="orange">
              {opportunities.length ? opportunities.length : "0"}
            </CustomHeadingCheckout>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', borderBottom: 'solid 1px #919191', paddingBlockEnd: '40px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <SaleIcon />
              <LabelAndNumberView
                ctx={{
                  heading: t("products").toUpperCase(),
                  number: createDecimalFormatter({ maximumFractionDigits: 0 }).format(sum),
                  text: `${opportunities.length} ${
                    opportunities.length === 1 ? t("opportunity").toLowerCase() : t("opportunities").toLowerCase()
                  }`
                }}
              />
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-center">
          <CustomHeadingCheckout size="md" color="grey" margin>
            {textFormatter(t("total"), { textTransform: 'uppercase' })}
          </CustomHeadingCheckout>
          <CustomHeadingCheckout size="lg" color="orange">
            {createDecimalFormatter({ maximumFractionDigits: 0 }).format(sum)}
          </CustomHeadingCheckout>
        </div>
      </div>
    </Card>
  );
};

/** Variáveis de ProductSummary */
const MAX_DESCRIPTION_LENGTH = 2000;
const language = localStorage.getItem('i18nextLng');

const ProductSummary = ({ actions, ctx }) => {

  const { handleFinishCheckoutOpportunity } = actions || {};

  const { opportunity, products } = ctx;

  const { opportunityId, productId, percent, textPt, textEn, value } = opportunity;

  const [openModal, setOpenModal] = useState(false);
  const [checkoutDescription, setCheckoutDescription] = useState("");  

  const onConcludedClick = async () => {
    try {
      await handleFinishCheckoutOpportunity({ opportunityId: opportunityId, checkoutDescription: checkoutDescription });
    } catch (error) {
      setOpenModal(false);
    } finally {
      setOpenModal(false);
      setCheckoutDescription("");
    }
  };

  return (
    <>
      <Card style={{ backgroundColor: "#fad6bf" }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px 10px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', borderBottom: 'solid 1px #919191', paddingBlockEnd: '40px' }}>
            <CustomHeadingCheckout size="md" color="grey">
              {textFormatter(t("checkoutSummary"), { textTransform: 'uppercase' })}
            </CustomHeadingCheckout>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', borderBottom: 'none', paddingBlockEnd: '0px' }}>
            <div className="d-flex align-items-center justify-content-center">
              <ProductHierarchyHeading ctx={{ products: products, productId: productId }}/>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', borderBottom: 'solid 1px #919191', paddingBlockEnd: '40px' }}>
            <TextAndLabelView
              label={t("increaseInSales")}
              labelSize="sm"
              heading={`${percent > 0 ? "↑" : "↓"} ${createPercentFormatter({
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(percent / 100)}`}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', borderBottom: 'none', paddingBlockEnd: '40px' }}>
            <div style={{ inlineSize: '100%' }}>
              <PredictionChart ctx={{ productId: productId }} />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', borderBottom: 'none', paddingBlockEnd: '40px' }}>
            <div className="d-flex align-items-center justify-content-center text-center">
              <p style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                {language === 'pt-BR' ? textPt : textEn}
              </p>
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-center">
            <CustomHeadingCheckout size="md" color="grey" margin>
              {textFormatter(t("total"), { textTransform: 'uppercase' })}
            </CustomHeadingCheckout>

            <CustomHeadingCheckout size="lg" color="orange">
              {currencyFormatter.format(value)}
            </CustomHeadingCheckout>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', borderBottom: 'none', paddingBlockEnd: '40px' }}>
            <div className="d-flex justify-content-end">
              <Button onClick={() => setOpenModal(true)} label={t("finalizeAction")} size="lg" />
            </div>
          </div>
        </div>
      </Card>

      <ModalTemplate 
        open={openModal} 
        width="500px" 
        closingFunction={() => { setOpenModal(false); setCheckoutDescription(""); }} 
        heading={t("finalizeOpportunity")}>

        <div style={{ marginTop: '1rem' }}>
          <CustomText weight="bold">
            {t("describeExecutedActions")}:
          </CustomText>

          <CustomText fontSize={['0.2rem', '0.4em', '0.6rem']} color={"red"} weight="bold">
            Item obrigatório*
          </CustomText>

          <textarea
            style={{ 
              inlineSize: '100%', 
              minBlockSize: '120px', 
              padding: '8px', 
              borderRadius: '4px', 
              border: '1px solid #ccc', 
              resize: 'vertical', 
              boxSizing: 'border-box', 
              marginBlockStart: '0.5rem' 
            }}
            rows="5"
            value={checkoutDescription}
            onChange={(e) => {
              if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) setCheckoutDescription(e.target.value);
            }}
            maxLength={MAX_DESCRIPTION_LENGTH}/>

          <CustomText textAlign="end" size="xs">
            {MAX_DESCRIPTION_LENGTH - checkoutDescription.length} {textFormatter(t("remainingCharacters"), { textTransform: 'lowercase' })}
          </CustomText>
        </div>

        <div className="d-flex justify-content-end mt-4">
          <Button onClick={() => { setOpenModal(false); setCheckoutDescription(""); }} label="Cancelar" type="outlined" className="" />
          <Button onClick={onConcludedClick} label={t("finish")} className="mx-3" disabled={!checkoutDescription.trim()} />
        </div>
      </ModalTemplate>
    </>
  );
};

export const Checkout = () => {
  const { t } = i18n;

  const [openRemovedOppModal, setOpenRemovedOppModal] = useState(false);

  const vm = useOtbViewModel({ shouldFetchCheckoutOpp: OPPORTUNITY_STATUS.CHECKOUT });
  const vmAnalysis = useAnalysisViewModel({ 
    shouldFetchProducts: true,
    shouldFetchForecastData: true
  })

  const listItemActions = {
    handleRemoveOpportunity: vm?.actions?.handleRemoveOpportunity,
    resetModalState: vm?.actions?.resetModalState,
    setSelectedId: vm?.actions?.setSelectedId,
    setRemovalReason: vm?.actions?.setRemovalReason,
  };

  const onItemClick = useCallback((opportunityId) => {
    if (opportunityId === vm.ctx.selectedId) vm.actions.setSelectedId(0);
    else vm.actions.setSelectedId(opportunityId);
  }, [vm.ctx.selectedId, vm.actions]);

  const listItemUiState = useMemo(() => ({ isCheckoutPage: true }), []);

  const opportunityOptionsActions = useMemo(() => ({
    toggleTypeFilter: vm?.actions?.toggleTypeFilter,
    setSelectedProductIds: vm?.actions?.setCheckoutSelectedProductIds,
    setOrder: vm?.actions?.setOrder
  }), [vm?.actions]);

  const opportunityOptionsCtx = useMemo(() => ({
    productIds: vm.ctx.checkoutOpportunities?.checkoutProductIds ?? [],
    selectedProductIds: vm.ctx.checkoutSelectedProductIds,
    typeOptions: vm.ctx.availableCheckoutTypes,
    selectedTypes: vm.ctx.checkoutOpportunities?.types,
    products: vmAnalysis?.ctx?.products
  }), [
    vm.ctx.checkoutOpportunities?.checkoutProductIds,
    vm.ctx.checkoutSelectedProductIds,
    vm.ctx.availableCheckoutTypes,
    vmAnalysis?.ctx?.products,
    vm.ctx.checkoutOpportunities?.types
  ]);

  const modals = useMemo(() => buildModalConfig({
    mode: 'opportunities',
    actions: {
      onClose: () => setOpenRemovedOppModal(false),
      onCloseError: () => vm?.actions?.resetOppError(),
    },
    ctx: {
      removedOpportunities: vm?.ctx?.removedOpportunities,
      opportunityTypes: vm?.ctx?.availableCheckoutTypes,
      errorStatus: vm.uiState.activeOpportunitiesError?.response?.status 
        || vm.uiState.checkoutOpportunitiesError?.response?.status
        || vm.uiState.updateOppError?.response?.status
        || 500,
      errorDetails: vm.uiState.activeOpportunitiesError?.response?.data?.details
        || vm.uiState.checkoutOpportunitiesError?.response?.data?.details
        || vm.uiState.updateOppError?.response?.data?.errors
        || [],
      errorPage: 'opportunities',
      operationType: vm?.actions?.definePatchOperationType({ uiState: vm?.uiState?.updateOppError }),
      products: vmAnalysis?.ctx?.products
    },
    components: {
      ModalRemovedOpp: RemovedOpportunitiesModal,
      ErrorModal: ErrorModal,
    },
    uiState: {
      isOpen: openRemovedOppModal,
      isErrorOpen: !!vm?.uiState?.updateOppError,
    }
  }), [
    vm?.actions,
    vm?.ctx?.removedOpportunities,
    vm?.ctx?.availableCheckoutTypes,
    vm?.uiState?.updateOppError,
    vm?.uiState?.activeOpportunitiesError,
    vm?.uiState?.checkoutOpportunitiesError,
    openRemovedOppModal,
    vmAnalysis?.ctx?.products
  ]);

  if (vm.uiState.isLoadingCheckoutOpportunities) 
    return <LoadingContent label={"Recuperando oportunidades em checkout..."} type={LOADING_CONTENT_TYPE.LOCAL_PAGE_STATE}/>

  return (
    <Default>
      <PageHeading padding={"0 0 0 1rem"}>{t("opportunitiesCheckout")}</PageHeading>
      <section style={{
        display: 'grid',
        gridTemplateColumns: 'auto 30%',
        gridTemplateRows: '1fr 1fr',
        gap: '20px',
        padding: '0 0 0 1rem'
      }}>
        <div>
          <OpportunityOptions
            actions={opportunityOptionsActions}
            ctx={opportunityOptionsCtx}
            uiState={{ order: vm.uiState.order, orderingMode: vm.uiState.orderOptions }}
          />

          {vm?.uiState?.sortedCheckoutOpportunities.map((opportunity) => (
            <ListItem 
              actions={listItemActions}
              ctx={{
                opportunity: opportunity,
                activeOppsWithTypes: vm.ctx.activeOpportunities?.activeOpportunities,
                options: vm?.ctx?.getOptions,
                removalReason: vm?.ctx?.removalReason,
                selectedId: vm?.ctx?.selectedId,
                products: vmAnalysis?.ctx?.products,
              }}
              uiState={listItemUiState}
              onItemClick={onItemClick}
              key={opportunity.opportunityId} 
            />
          ))}
        </div>

        {vm.ctx.selectedId === 0 || vm.ctx.checkoutOpportunities?.checkoutOpportunities?.length === 0 || !vm.uiState.selected ? (
          <CheckoutSummary ctx={{ opportunities: vm.uiState.sortedCheckoutOpportunities }} />
        ) : (
          <ProductSummary 
            actions={{ handleFinishCheckoutOpportunity: vm?.actions?.handleFinishCheckoutOpportunity }}
            ctx={{ 
              products: vmAnalysis?.ctx?.products,
              opportunity: vm.uiState.selected 
            }} />
        )}
      </section>

      {renderModal(modals)}

      <ModalTemplate 
        open={!!vm?.ctx?.confirmationMessage} 
        width="300px" 
        closingFunction={vm?.actions?.resetModalState} 
        heading=" ">
        <CustomHeading size="sm" margin="1.5rem 0 0 0" textAlign="center">
          {vm?.ctx?.confirmationMessage}
        </CustomHeading>
        <div style={{ display: 'flex', justifyContent: 'center', marginBlockStart: '1.5rem' }}>
          <Button label="Ok" onClick={vm?.actions?.resetModalState} />
        </div>
      </ModalTemplate>
      
    </Default>
  );
};

ProductSummary.propTypes = { ctx: PropTypes.shape() };
CheckoutSummary.propTypes = { ctx: PropTypes.shape() };